import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { INVITACIONES_MOCK } from '../mocks/invitaciones.mock';
import { InvitacionPostulacion, RecomendacionInvitacion, RolInvitacion } from '../models/invitaciones.models';
import { PostulacionesService } from './postulaciones.service';

@Injectable({ providedIn: 'root' })
export class InvitacionesService {
  private readonly baseUrl = `${environment.apiBaseUrl}/invitaciones`;
  private readonly invitacionesMock$ = new BehaviorSubject<InvitacionPostulacion[]>(INVITACIONES_MOCK);

  constructor(
    private readonly http: HttpClient,
    private readonly postulacionesService: PostulacionesService
  ) {}

  getInvitacionesPorPostulaciones(idsPostulacion: string[]): Observable<InvitacionPostulacion[]> {
    if (idsPostulacion.length === 0) {
      return of([]);
    }

    if (!environment.useMockApi) {
      return this.http.get<InvitacionPostulacion[]>(`${this.baseUrl}/postulante`, {
        params: { ids_postulacion: idsPostulacion.join(',') },
      });
    }

    return this.invitacionesMock$.pipe(
      map((items) => items.filter((item) => idsPostulacion.includes(item.idPostulacion)))
    );
  }

  crearInvitacion(payload: {
    idPostulacion: string;
    concurso: string;
    proyecto: string;
    subdireccion: string;
    folio: string;
    rolDestino: Extract<RolInvitacion, 'colaborador' | 'recomendador'>;
    nombresDestino: string;
    apellidosDestino: string;
    rutDestino: string;
    emailDestino: string;
    institucionAsociada?: string;
  }): Observable<InvitacionPostulacion | null> {
    if (!environment.useMockApi) {
      return this.http.post<InvitacionPostulacion>(this.baseUrl, payload);
    }

    const nuevaInvitacion: InvitacionPostulacion = {
      id: this.generarIdInvitacion(payload.rolDestino),
      idPostulacion: payload.idPostulacion,
      fechaSolicitud: new Date().toISOString(),
      concurso: payload.concurso,
      proyecto: payload.proyecto,
      subdireccion: payload.subdireccion,
      folio: payload.folio,
      rolDestino: payload.rolDestino,
      nombresDestino: payload.nombresDestino,
      apellidosDestino: payload.apellidosDestino,
      rutDestino: payload.rutDestino,
      emailDestino: payload.emailDestino,
      institucionAsociada: payload.institucionAsociada,
      notificacionEnviada: true,
      estadoRespuesta: 'pendiente',
      asociadaAlProceso: false,
      cartaGuardada: false,
      cartaEnviada: false,
    };

    this.invitacionesMock$.next([nuevaInvitacion, ...this.invitacionesMock$.value]);
    return of(nuevaInvitacion);
  }

  getInvitacionesPorRol(rol: RolInvitacion): Observable<InvitacionPostulacion[]> {
    if (!environment.useMockApi) {
      return this.http.get<InvitacionPostulacion[]>(`${this.baseUrl}?rol=${rol}`);
    }

    return this.invitacionesMock$.pipe(map((items) => items.filter((item) => item.rolDestino === rol)));
  }

  responderInvitacion(
    idInvitacion: string,
    rol: RolInvitacion,
    estadoRespuesta: 'aceptada' | 'rechazada'
  ): Observable<InvitacionPostulacion | null> {
    if (!environment.useMockApi) {
      return this.http.post<InvitacionPostulacion>(`${this.baseUrl}/${idInvitacion}/respuesta`, {
        rol,
        estado_respuesta: estadoRespuesta,
      });
    }

    const actualizadas = this.invitacionesMock$.value.map((item) =>
      item.id === idInvitacion
        ? {
            ...item,
            estadoRespuesta,
            fechaRespuesta: new Date().toISOString(),
            asociadaAlProceso: estadoRespuesta === 'aceptada',
          }
        : item
    );

    this.invitacionesMock$.next(actualizadas);

    const respuesta = actualizadas.find((item) => item.id === idInvitacion) ?? null;
    if (respuesta) {
      this.postulacionesService.actualizarParticipantePorInvitacion(respuesta, estadoRespuesta);
    }

    return of(respuesta);
  }

  guardarRecomendacion(idInvitacion: string, recomendacion: RecomendacionInvitacion): Observable<InvitacionPostulacion | null> {
    if (!environment.useMockApi) {
      return this.http.post<InvitacionPostulacion>(`${this.baseUrl}/${idInvitacion}/recomendacion/guardar`, recomendacion);
    }

    const actualizadas = this.invitacionesMock$.value.map((item) =>
      item.id === idInvitacion
        ? {
            ...item,
            cartaGuardada: true,
            recomendacion,
          }
        : item
    );

    this.invitacionesMock$.next(actualizadas);
    return of(actualizadas.find((item) => item.id === idInvitacion) ?? null);
  }

  enviarRecomendacion(idInvitacion: string, recomendacion: RecomendacionInvitacion): Observable<InvitacionPostulacion | null> {
    if (!environment.useMockApi) {
      return this.http.post<InvitacionPostulacion>(`${this.baseUrl}/${idInvitacion}/recomendacion/enviar`, recomendacion);
    }

    const actualizadas = this.invitacionesMock$.value.map((item) =>
      item.id === idInvitacion
        ? {
            ...item,
            estadoRespuesta: 'aceptada' as const,
            fechaRespuesta: new Date().toISOString(),
            asociadaAlProceso: true,
            cartaGuardada: true,
            cartaEnviada: true,
            recomendacion,
          }
        : item
    );

    this.invitacionesMock$.next(actualizadas);

    const respuesta = actualizadas.find((item) => item.id === idInvitacion) ?? null;
    if (respuesta) {
      this.postulacionesService.actualizarParticipantePorInvitacion(respuesta, 'aceptada');
    }

    return of(respuesta);
  }

  private generarIdInvitacion(rolDestino: Extract<RolInvitacion, 'colaborador' | 'recomendador'>): string {
    const prefijo = rolDestino === 'recomendador' ? 'INV-3' : 'INV-2';
    const max = this.invitacionesMock$.value.reduce((acumulado, item) => {
      const numero = Number.parseInt(item.id.replace(/\D/g, ''), 10);
      return Number.isNaN(numero) ? acumulado : Math.max(acumulado, numero);
    }, rolDestino === 'recomendador' ? 3000 : 2000);

    return `${prefijo}${String(max + 1).slice(-3)}`;
  }
}
